import React, { useId, useState } from 'react';
import { RotateCcw } from 'lucide-react';
import { apiService } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import { SettingRow, formatSetting } from './types';

const PRESS = 'transition-[transform,background-color,color,box-shadow] duration-150 active:scale-[0.98] motion-reduce:transform-none';

/** Why the typed value cannot be saved, or null. Checked as you type, with the same range the server enforces. */
function problem(s: SettingRow, draft: string): string | null {
  if (s.unit === 'date') return /^\d{4}-\d{2}-\d{2}$/.test(draft) && !Number.isNaN(Date.parse(draft)) ? null : 'Pick a date';
  if (draft.trim() === '' || Number.isNaN(Number(draft))) return 'Enter a number';
  const n = Number(draft);
  if (s.unit !== 'percent' && !Number.isInteger(n)) return 'Use a whole number';
  if (s.min !== null && s.max !== null && (n < s.min || n > s.max)) return `Between ${s.min} and ${s.max}`;
  return null;
}

// One standard: what it means, its value, the built-in value beside it, and Save and Reset. The check
// runs while you type, not after you press Save. Nothing is saved until you do.
export default function SettingEditor({ setting, onSaved }: { setting: SettingRow; onSaved: () => void }) {
  const { showToast } = useToast();
  const id = useId();
  const [draft, setDraft] = useState(String(setting.value));
  const [busy, setBusy] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  const changed = draft !== String(setting.value);
  const issue = changed ? problem(setting, draft) : null;

  const run = async (action: () => Promise<any>, done: string) => {
    setBusy(true);
    setServerError(null);
    try {
      const r = await action();
      if (!r.success) throw new Error(r.error || 'Could not save');
      setDraft(String(r.data.value));
      showToast('success', done);
      onSaved();
    } catch (e: any) {
      setServerError(e?.response?.data?.error || e?.message || 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  const save = () => {
    if (!changed || issue || busy) return;
    void run(() => apiService.setStandardSetting(setting.area, setting.key, setting.unit === 'date' ? draft : Number(draft)), `${setting.label} saved. It applies to every month.`);
  };
  const reset = () => void run(() => apiService.resetStandardSetting(setting.area, setting.key), `${setting.label} is back to ${formatSetting(setting, setting.default)}.`);

  return (
    <div className="grid gap-3 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-gray-900">{setting.label}</label>
        <p className="mt-0.5 text-xs leading-snug text-gray-500">{setting.description}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
        <div className="relative">
          <input
            id={id}
            type={setting.unit === 'date' ? 'date' : 'number'}
            inputMode={setting.unit === 'date' ? undefined : 'decimal'}
            value={draft}
            min={setting.min ?? undefined}
            max={setting.max ?? undefined}
            step={setting.unit === 'percent' ? 'any' : 1}
            onChange={(e) => {
              setDraft(e.target.value);
              setServerError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') setDraft(String(setting.value));
            }}
            aria-invalid={!!issue}
            aria-describedby={`${id}-hint`}
            className={`w-36 rounded-lg border bg-white py-2 pl-3 text-sm tabular-nums text-gray-900 shadow-sm transition focus:outline-none focus:ring-2 ${setting.unit === 'percent' || setting.unit === 'days' ? 'pr-10' : 'pr-3'} ${
              issue ? 'border-rose-300 focus:border-rose-400 focus:ring-rose-100' : 'border-gray-200 focus:border-blue-400 focus:ring-blue-100'
            }`}
          />
          {(setting.unit === 'percent' || setting.unit === 'days') && (
            <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">{setting.unit === 'percent' ? '%' : 'days'}</span>
          )}
        </div>
        <button
          type="button"
          onClick={save}
          disabled={!changed || !!issue || busy}
          className={`rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500 disabled:shadow-none ${PRESS} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        {!setting.is_default && !changed && (
          <button
            type="button"
            onClick={reset}
            disabled={busy}
            title={`Back to ${formatSetting(setting, setting.default)}`}
            className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 hover:text-gray-900 ${PRESS} focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400`}
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden />
            Reset
          </button>
        )}
        <p id={`${id}-hint`} className={`basis-full text-xs sm:text-right ${issue || serverError ? 'text-rose-700' : 'text-gray-500'}`} role={issue || serverError ? 'alert' : undefined}>
          {issue ?? serverError ?? `Built-in: ${formatSetting(setting, setting.default)}`}
        </p>
      </div>
    </div>
  );
}
